import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import ServiceCard from '../components/ServiceCard';
import { services, stats, whyChooseUs } from '../utils/constants';

const Home = () => {
    // Service photos mapping - Using Unsplash images relevant to each service
    const servicePhotos = {
        'HVAC Systems': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'Electrical Works': 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'Plumbing Services': 'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'Waterproofing Solutions': 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'Civil Construction': 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'Metal & Aluminum Work': 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'Painting Services': 'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'Carpentry & Woodwork': 'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'Tiling Works': 'https://images.unsplash.com/photo-1600066948854-e5d8d1497135?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    };

    // Add photos to services data
    const servicesWithPhotos = services.slice(0, 6).map(service => ({
        ...service,
        photo: servicePhotos[service.title] || 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }));

    // Client logos data
    const clientLogos = [
        { id: 1, name: 'Akar', src: '/assets/images/aqaar.jpg', alt: 'Akar Company Logo' },
        { id: 2, name: 'Grand', src: '/assets/images/grand.jpg', alt: 'Grand Company Logo' },
        { id: 3, name: 'Marriott', src: '/assets/images/marriot.jpg', alt: 'Marriott Hotel Logo' },
        { id: 4, name: 'Park', src: '/assets/images/park.jpg', alt: 'Park Company Logo' },
        { id: 5, name: 'Radisson', src: '/assets/images/radisson.jpg', alt: 'Radisson Hotel Logo' },
        { id: 6, name: 'Agency', src: '/assets/images/regency.jpg', alt: 'Agency Logo' },
        { id: 7, name: 'Tech', src: '/assets/images/tech.jpg', alt: 'Tech Company Logo' }
    ];

    return (
        <>
            <Helmet>
                <title>LAMSAT | Premium Engineering Services in Dubai</title>
                <meta
                    name="description"
                    content="LAMSAT Omer & Mohammed Technical Services LLC - Your ultimate one-stop Engineering Service Provider in Dubai, UAE. HVAC, Electrical, Plumbing, Civil Construction."
                />
                <meta property="og:title" content="LAMSAT | Premium Engineering Services in Dubai" />
                <meta property="og:description" content="Leading provider of electrical, plumbing, waterproofing, and civil construction services in UAE." />
                <meta property="og:type" content="website" />
            </Helmet>

            {/* Hero Section with Local Dubai Video Background */}
            <section className="hero-section">
                <div className="video-container">
                    <video
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="hero-video"
                        poster="/assets/videos/dubai-night.png"
                    >
                        <source
                            src="/assets/videos/dubai.mp4"
                            type="video/mp4"
                        />
                        <div className="video-fallback">
                            <img
                                src="/assets/videos/dubai-night.png"
                                alt="Dubai Engineering Landscape"
                                className="fallback-image"
                            />
                        </div>
                        Your browser does not support the video tag.
                    </video>
                </div>
                <div className="hero-overlay"></div>
                <div className="container hero-content">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1 }}
                        className="row align-items-center justify-content-center"
                    >
                        <div className="col-lg-10 col-xl-8 text-center">
                            {/* Logo Section - Responsive adjustments */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="mb-3 mb-md-4 pt-2 pt-md-4"
                            >
                                <div className="logo-container mx-auto">
                                    <img
                                        src="/assets/images/logo-2.png"
                                        alt="LAMSAT Engineering Logo"
                                        className="hero-logo img-fluid"
                                        style={{
                                            maxWidth: '280px',
                                            width: '100%',
                                            height: 'auto',
                                            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                                            marginBottom: '0.75rem'
                                        }}
                                        onError={(e) => {
                                            e.target.src = 'https://via.placeholder.com/280x80/2E2F5B/ffffff?text=LAMSAT+Engineering';
                                            e.target.style.width = '280px';
                                            e.target.style.height = '80px';
                                        }}
                                    />
                                </div>

                                {/* Tagline positioned closer to logo */}
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.4 }}
                                    className="mt-1 mt-md-2"
                                >
                                    <p className="text-white" style={{
                                        fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)',
                                        color: 'rgba(255, 255, 255, 0.85)',
                                        letterSpacing: '0.5px',
                                        lineHeight: '1.4',
                                        padding: '0 0.5rem'
                                    }}>
                                        Your ultimate one-stop Engineering Service Provider
                                    </p>
                                </motion.div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.6 }}
                                className="mt-2 mt-md-3"
                            >
                                <h1 className="display-4 fw-bold mb-3 mb-md-4 text-white" style={{
                                    fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
                                    lineHeight: '1.2',
                                    marginTop: '1rem',
                                    padding: '0 0.5rem'
                                }}>
                                    Premium <span className="text-primary-red">Engineering Solutions</span><br className="d-none d-md-block" />
                                    <span className="d-inline d-md-none"> </span>for Dubai & UAE
                                </h1>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.8 }}
                            >
                                <p className="lead mb-4 mb-md-5 text-white hero-subtitle" style={{
                                    fontSize: 'clamp(0.95rem, 3vw, 1.1rem)',
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    lineHeight: '1.5',
                                    padding: '0 0.5rem'
                                }}>
                                    Specializing in large-scale projects with over 30 years of expertise.<br className="d-none d-md-block" />
                                    We deliver high-quality engineering solutions that ensure durability, safety, and efficiency.
                                </p>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 1 }}
                                className="d-flex flex-wrap justify-content-center gap-3 gap-md-4 mt-3 mt-md-4"
                            >
                                <a
                                    href="/contact"
                                    className="btn btn-primary-custom btn-hero"
                                    style={{
                                        padding: '12px 24px',
                                        fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                                        minWidth: '180px'
                                    }}
                                >
                                    <i className="fas fa-envelope me-2"></i>
                                    Get Free Quote
                                </a>
                                <a
                                    href="/services"
                                    className="btn btn-outline-custom btn-hero"
                                    style={{
                                        padding: '12px 24px',
                                        fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                                        minWidth: '180px'
                                    }}
                                >
                                    <i className="fas fa-wrench me-2"></i>
                                    Explore Our Services
                                </a>
                            </motion.div>

                            {/* Company Credentials */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.8, delay: 1.2 }}
                                className="mt-4 mt-md-5 pt-2 pt-md-3"
                            >
                                <div className="credentials-badge d-inline-block px-3 px-md-4 py-1 py-md-2 rounded-pill" style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    maxWidth: '90%'
                                }}>
                                    <p className="mb-0 text-white small" style={{
                                        fontSize: 'clamp(0.7rem, 1.8vw, 0.85rem)',
                                        lineHeight: '1.3'
                                    }}>
                                        <span className="fw-bold">TRN:</span> 104929219400003 •
                                        <span className="fw-bold ms-2 ms-md-3">License:</span> 880897 •
                                        <span className="fw-bold ms-2 ms-md-3">Registration:</span> 2552101
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Scroll Indicator */}
                    <motion.div
                        className="scroll-indicator d-none d-md-block"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.4 }}
                    >
                        <a href="#services" className="scroll-down">
                            <i className="fas fa-chevron-down"></i>
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-4 py-md-5 bg-off-white">
                <div className="container">
                    <div className="row">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                className="col-md-3 col-6 mb-3 mb-md-4"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <div className="stat-card text-center p-2 p-md-3">
                                    <div className="stat-number" style={{
                                        fontSize: 'clamp(1.5rem, 4vw, 2.5rem)'
                                    }}>{stat.value}</div>
                                    <h6 className="mb-0 small" style={{
                                        fontSize: 'clamp(0.8rem, 2vw, 0.9rem)'
                                    }}>{stat.label}</h6>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Preview */}
            <section id="services" className="py-4 py-md-5">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="text-center mb-4 mb-md-5"
                    >
                        <h2 className="section-title d-inline-block" style={{
                            fontSize: 'clamp(1.5rem, 4vw, 2rem)'
                        }}>Our Services</h2>
                        <p className="text-muted mt-2" style={{
                            fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                        }}>Comprehensive engineering solutions for large-scale projects</p>
                    </motion.div>

                    <div className="row">
                        {servicesWithPhotos.map((service, index) => (
                            <motion.div
                                key={service.id}
                                className="col-lg-4 col-md-6 mb-4"
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <div className="service-card-photo h-100">
                                    {/* Service Photo */}
                                    <div className="service-photo-container" style={{ height: '200px' }}>
                                        <img
                                            src={service.photo}
                                            alt={service.title}
                                            className="service-photo w-100 h-100 object-fit-cover"
                                            loading="lazy"
                                            onError={(e) => {
                                                e.target.src = 'https://via.placeholder.com/400x200/2E2F5B/ffffff?text=' + service.title;
                                            }}
                                        />
                                        <div className="service-photo-overlay"></div>
                                    </div>

                                    {/* Service Content */}
                                    <div className="service-content p-3">
                                        <h5 className="service-title mb-2" style={{
                                            fontSize: '1.1rem'
                                        }}>{service.title}</h5>
                                        <p className="service-description text-muted mb-3 small">{service.description}</p>

                                        <div className="service-features">
                                            {service.features && service.features.slice(0, 2).map((feature, i) => (
                                                <div key={i} className="d-flex align-items-center mb-1">
                                                    <i className="fas fa-check-circle text-primary-red me-2 small"></i>
                                                    <span className="small">{feature}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <a href="/services" className="btn btn-outline-custom btn-sm mt-2 d-inline-flex align-items-center">
                                            Learn More <i className="fas fa-arrow-right ms-2 small"></i>
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        viewport={{ once: true }}
                        className="text-center mt-4 mt-md-5"
                    >
                        <a href="/services" className="btn btn-outline-custom px-4">
                            View All Services <i className="fas fa-arrow-right ms-2"></i>
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-4 py-md-5 bg-primary-blue text-white">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="text-center mb-4 mb-md-5"
                    >
                        <h2 className="section-title d-inline-block text-white" style={{
                            fontSize: 'clamp(1.5rem, 4vw, 2rem)'
                        }}>Why Choose Us</h2>
                        <p className="mt-2" style={{
                            fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                        }}>Excellence in every project we undertake</p>
                    </motion.div>

                    <div className="row">
                        {whyChooseUs.map((item, index) => (
                            <motion.div
                                key={index}
                                className="col-md-4 mb-3 mb-md-4"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <div className="text-center p-3 p-md-4 h-100">
                                    <div className="feature-icon mx-auto mb-3" style={{
                                        width: '60px',
                                        height: '60px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        borderRadius: '50%'
                                    }}>
                                        <i className={`fas ${item.icon} fa-lg`}></i>
                                    </div>
                                    <h5 className="mb-2" style={{ fontSize: '1.1rem' }}>{item.title}</h5>
                                    <p className="mb-0 small" style={{ color: '#ccc' }}>{item.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Clients Section */}
            <section className="py-4 py-md-5 bg-white" style={{ backgroundColor: 'var(--off-white)' }}>
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="text-center mb-4 mb-md-5"
                    >
                        <h2 className="section-title d-inline-block" style={{
                            fontSize: 'clamp(1.5rem, 4vw, 2rem)'
                        }}>Our Esteemed Clients</h2>
                        <p className="text-muted mb-3 mb-md-4 mt-2" style={{
                            fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                        }}>Trusted by leading companies and organizations across UAE</p>
                    </motion.div>

                    {/* Client Logos Grid */}
                    <div className="row justify-content-center align-items-center g-3 g-md-4">
                        {clientLogos.map((client, index) => (
                            <motion.div
                                key={client.id}
                                className="col-4 col-sm-3 col-md-3 col-lg-2"
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{
                                    duration: 0.5,
                                    delay: index * 0.1,
                                    type: "spring",
                                    stiffness: 100
                                }}
                                viewport={{ once: true, margin: "-50px" }}
                                whileHover={{
                                    scale: 1.05,
                                    transition: { duration: 0.2 }
                                }}
                            >
                                <div className="client-logo-container position-relative">
                                    {/* Logo Card */}
                                    <div className="client-logo-card position-relative overflow-hidden rounded-2 rounded-md-3 p-2 p-md-3 p-lg-4 bg-white shadow-sm"
                                        style={{
                                            height: '80px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '1px solid rgba(0,0,0,0.05)',
                                            transition: 'all 0.3s ease'
                                        }}>

                                        {/* Logo Image */}
                                        <motion.img
                                            src={client.src}
                                            alt={client.alt}
                                            className="position-relative z-2 img-fluid object-fit-contain"
                                            style={{
                                                maxHeight: '40px',
                                                maxWidth: '80px',
                                                filter: 'grayscale(1)',
                                                transition: 'all 0.3s ease',
                                                opacity: 0.7
                                            }}
                                            whileHover={{
                                                filter: 'grayscale(0)',
                                                opacity: 1
                                            }}
                                            onError={(e) => {
                                                e.target.src = 'https://via.placeholder.com/80x40/2E2F5B/ffffff?text=' + client.name;
                                            }}
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Client Statistics */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mt-4 mt-md-5 pt-2 pt-md-3"
                    >
                        <div className="row justify-content-center">
                            <div className="col-md-8 col-lg-6">
                                <div className="bg-white p-3 p-md-4 rounded-3 shadow-sm">
                                    <p className="text-muted mb-0 small">
                                        <i className="fas fa-check-circle text-primary-red me-2"></i>
                                        Over 50+ satisfied clients across Dubai and UAE
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-4 py-md-5">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="bg-primary-blue rounded-3 p-4 p-md-5 text-center text-white"
                    >
                        <h3 className="mb-3 mb-md-4" style={{
                            fontSize: 'clamp(1.3rem, 3.5vw, 1.8rem)'
                        }}>Ready to Start Your Project?</h3>
                        <p className="mb-3 mb-md-4 small" style={{ color: '#ccc' }}>
                            Contact us today for a free consultation and quote. Let's build something great together.
                        </p>
                        <a href="/contact" className="btn btn-primary-custom px-4 py-2">
                            <i className="fas fa-phone me-2"></i> Contact Us Now
                        </a>
                    </motion.div>
                </div>
            </section>
        </>
    );
};

export default Home;